import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Link, useLocation } from '@tanstack/react-router';
import { Icon } from '@frontend/icons';
import { useNavSize } from '@frontend/responsiveness';
import { useLocationService } from '@frontend/wam-location-service';
import { theme } from '@frontend/theme';
import { Button, Heading, NakedUl, Text } from '@frontend/design-system';
import { useLayout } from '../../providers/layout.provider';
import { NavSearch } from '../search';
import { useGlobalNavItems, useMainNavItems, type NavLink } from './use-nav-items';

const SettingsMobileHeader = () => {
  const { closeNav } = useLayout();

  return (
    <header
      css={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2, 0, 3),
      }}
    >
      <Heading css={{ margin: 0 }} level={2} color='white' textAlign='left'>
        Navigation
      </Heading>
      <Button
        iconName='x'
        color='white'
        css={{
          background: 'transparent',
          '&:hover, :focus': {
            background: theme.colors.neutral80,
          },
        }}
        onClick={() => closeNav()}
      />
    </header>
  );
};

const navItemStyle = (selected: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.colors.white,
  background: selected ? theme.colors.primary50 : 'none',
  fontWeight: selected ? theme.font.weight.semibold : 'normal',
  padding: theme.spacing(0.5, 2),
  borderRadius: theme.borderRadius.small,
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background 250ms ease-in-out',
  width: '100%',
});

const NavItem = ({
  disabled,
  expand = false,
  iconName,
  label,
  onClick,
  subnav,
  to,
  trackingId,
}: NavLink & { expand?: boolean; onClick?: (label: string) => void }) => {
  const location = useLocation();
  const topLevelPath = useMemo(() => location.pathname.split('/')[1], [location.pathname]);
  const subnavLevelPathTo = useMemo(() => location.pathname.split('/')[2], [location.pathname]);

  const checkIfTopLevelPath = () => to?.split('/')[1] === topLevelPath;

  const checkIfSubnavLevelPath = (path: string) => {
    const currentSubnavLevelPath = path.split('/')?.[2];
    return currentSubnavLevelPath === subnavLevelPathTo;
  };

  if (disabled) {
    return (
      <div
        css={{
          display: 'flex',
          color: theme.colors.neutral50,
          padding: theme.spacing(0.5, 2),
          borderRadius: theme.borderRadius.small,
          cursor: 'not-allowed',
          width: '100%',
        }}
      >
        {iconName && <Icon name={iconName} css={{ marginRight: theme.spacing(1) }} />}
        <Text as='span'>{label}</Text>
        {subnav && subnav.length > 0 && <Icon name='caret-down' css={{ marginLeft: 'auto' }} />}
      </div>
    );
  }

  if (subnav && subnav.length > 0) {
    return (
      <div>
        <Button
          onClick={() => onClick?.(label)}
          css={[
            navItemStyle(false),
            {
              justifyContent: 'space-between',
              cursor: 'pointer',
              ':hover, :focus, :target, :active': {
                background: 'none',
              },
              '& > span': {
                display: 'flex',
                width: '100%',
                color: theme.colors.white,
              },
            },
          ]}
        >
          {iconName && <Icon name={iconName} css={{ marginRight: theme.spacing(1) }} />}
          <Text as='span' color='white'>
            {label}
          </Text>
          <Icon
            name='caret-down'
            css={{
              marginLeft: 'auto',
              transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 250ms ease-in-out',
            }}
          />
        </Button>
        {expand && subnav && (
          <div
            css={{ marginTop: theme.spacing(1), display: 'flex', flexDirection: 'column', rowGap: theme.spacing(1) }}
          >
            {subnav.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => onClick?.(item.label)}
                data-trackingid={trackingId}
                css={[navItemStyle(checkIfSubnavLevelPath(item.to ?? '')), { padding: theme.spacing(0.5, 2, 0.5, 4) }]}
              >
                {item.iconName && <Icon name={item.iconName} css={{ marginRight: theme.spacing(1) }} />}
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link to={to} data-trackingid={trackingId} css={navItemStyle(checkIfTopLevelPath())}>
      {iconName && <Icon name={iconName} css={{ marginRight: theme.spacing(1) }} />}
      {label}
    </Link>
  );
};

const useNavItemExpansion = (navItems: NavLink[]) => {
  const [expandedLabel, setExpandedLabel] = useState<string>();
  const onNavItemClick = (label: string) => {
    if (navItems.find((item) => item.label === label)?.subnav?.length ?? 0 > 0) {
      setExpandedLabel(label === expandedLabel ? undefined : label);
    }
  };
  return { expandedLabel, onNavItemClick };
};

export const SideNav = () => {
  const { currentLocationId } = useLocationService();
  const mainNavItems = useMainNavItems();
  const footerNavItems = useGlobalNavItems();

  const navSize = useNavSize();
  const isSmallerThanDesktop = navSize.isLt('large');
  const isMini = navSize.isGte('small') && navSize.isLte('mobile');

  const { isNavOpen } = useLayout();

  const { expandedLabel: mainNavExpandedLabel, onNavItemClick: onMainNavClick } = useNavItemExpansion(mainNavItems);
  const { expandedLabel: footerNavExpandedLabel, onNavItemClick: onFooterNavClick } =
    useNavItemExpansion(footerNavItems);

  return (
    <aside
      css={[
        !isSmallerThanDesktop && {
          maxWidth: 248,
          minWidth: 248,
          maxHeight: '100%',
        },
        isSmallerThanDesktop && {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          left: '-100%',
          position: 'fixed',
          transition: 'left 0.3s ease-in-out',
          width: isMini ? 400 : '100%',
          zIndex: 2,
          backgroundColor: theme.colors.neutral90,
          top: 0,
        },
        isSmallerThanDesktop &&
          isNavOpen && {
            left: 0,
          },
      ]}
    >
      {isSmallerThanDesktop && <SettingsMobileHeader />}

      <nav
        css={{
          paddingTop: isSmallerThanDesktop ? 0 : theme.spacing(2),
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.colors.neutral90,
          width: '100%',
          svg: {
            width: 16,
            height: 16,
            minWidth: 0,
          },
        }}
      >
        {currentLocationId && <NavSearch />}
        <NakedUl
          css={{
            padding: theme.spacing(2),
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            rowGap: theme.spacing(2),
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: theme.spacing(0.5),
              height: theme.spacing(0.5),
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.colors.neutral50,
              borderRadius: theme.borderRadius.small,
            },
          }}
        >
          {mainNavItems
            .filter(({ hide }) => !hide)
            .map((item) => (
              <NavItem
                key={item.label}
                expand={item.label === mainNavExpandedLabel}
                onClick={onMainNavClick}
                {...item}
              />
            ))}
        </NakedUl>
        <NakedUl
          css={{
            padding: theme.spacing(2),
            borderTop: `1px solid ${theme.colors.neutral60}`,
            display: 'flex',
            flexDirection: 'column',
            rowGap: theme.spacing(2),
          }}
        >
          {footerNavItems.map((item) => (
            <NavItem
              key={item.label}
              expand={item.label === footerNavExpandedLabel}
              onClick={onFooterNavClick}
              {...item}
            />
          ))}
        </NakedUl>
        <footer
          css={{
            borderTop: `1px solid ${theme.colors.neutral60}`,
            padding: theme.spacing(2),
            textAlign: 'center',
          }}
        >
          {currentLocationId && (
            <a
              css={css`
                color: white;
                text-decoration: none;
              `}
              href={`https://app.getweave.com/admin/${currentLocationId}/dashboard`}
              target='_blank'
              rel='noreferrer'
            >
              Open Location in NWX
            </a>
          )}
        </footer>
      </nav>
    </aside>
  );
};
